/**
 * selectedImportStorage — the persisted key for the app-wide import selection,
 * and the one operation that must be reachable from OUTSIDE the feature.
 *
 * Lives in `lib/` rather than in `features/imports/selection/` for a layering
 * reason: the typed API clients (`clients/*.client.ts`) have to clear the
 * selection from their session-expiry handlers, and `clients/` → `features/`
 * would invert the dependency direction the rest of the package follows. The
 * feature module re-exports both symbols, so feature code still has a single
 * import path.
 *
 * Why the clear matters: the stored projection holds another recruiter's data
 * — filename, owner name/email, the linked Thrive search — so every path that
 * ends a session must drop it, not just the two the UI owns.
 */

/**
 * Bump the suffix instead of migrating a changed shape.
 *
 * DELIBERATELY still `v1` after `searchIdThrive` was added to the projection.
 * Bumping is for when a stale value must not be READ; here
 * `parseStoredSelectedImport` already rejects the old shape (the new field is
 * absent, so validation returns `null`) and the selection simply starts empty —
 * the same user-visible outcome a bump would produce.
 *
 * What a bump would ALSO do is strand the `v1` blob under a key nothing clears.
 * Per the note above, that blob holds another recruiter's data, and
 * `clearStoredSelectedImport` only removes the key named here — so a bump would
 * leave PII on every existing machine with no path to delete it. Keeping the key
 * means logout still purges it and the next selection overwrites it.
 *
 * So: bump when the KEY needs to change semantics, not merely when a field is
 * added that validation can reject on its own.
 */
export const SELECTED_IMPORT_STORAGE_KEY = "nu-assist.selectedImport.v1";

export function clearStoredSelectedImport(): void {
  try {
    window.localStorage.removeItem(SELECTED_IMPORT_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing was persisted to leak.
  }
}
